import React, { useState } from "react";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { Button, IconButton, Text } from "@commonsku/styles";
import { useDispatch } from "react-redux";
import { loadSavedReport } from "../../../../redux/savedReports";
import DeleteReportPopup from "../../action-menu/DeleteReportPopup";
import { useReportContext } from "../../report_context";
import styled from "styled-components";
import { closePopup, createScheduleReportPopup } from "../../../../actions/popup";

const ReportNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> =
  {
    header: "Report Name",
    type: BASE_COLUMN_TYPES.Text,
    fields: ["report_name"],
    accessorKey: "report_name",
    canSort: true,
    orderByField: "report_name",
    width: 120,
    textOptions: {
      truncate: true,
    },
  };

const ReportTypeColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> =
  {
    header: "Report Type",
    type: BASE_COLUMN_TYPES.Text,
    fields: ["report_type_title", "report_type_name"],
    accessorKey: "report_type",
    canSort: true,
    orderByField: "report_type_title",
    width: 178,
    dataTransform: ({ report_type_title }) => ({ text: report_type_title }),
  };

const LastUpdatedColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> =
  {
    header: "Last Updated",
    type: BASE_COLUMN_TYPES.Date,
    fields: ["date_created"],
    accessorKey: "date_created",
    canSort: true,
    orderByField: "date_created",
    width: 120,
  };

const ScheduleColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> =
  {
    header: "Scheduling",
    type: BASE_COLUMN_TYPES.Generic,
    fields: [
      "report_id",
      "scheduled_report_id",
      "frequency",
      "month_day",
      "week_day",
      "scheduled_report_date_created",
    ],
    accessorKey: "schedule",
    canSort: true,
    orderByField: "scheduled_report_date_created",
    width: 180,
    justify: "center",
    capabilities: ["EXPORT-REPORT", "HAS-SCHEDULED-REPORTS"],
    dataTransform: ({
      report_id,
      scheduled_report_id,
      frequency,
      month_day,
      week_day,
    }) => ({
      cell: (
        <ScheduleCell
          report_id={report_id}
          scheduled_report_id={scheduled_report_id}
          frequency={frequency}
          month_day={month_day}
          week_day={week_day}
        />
      ),
    }),
  };

const ActionsColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Generic> =
  {
    header: "Actions",
    type: BASE_COLUMN_TYPES.Generic,
    fields: ["report_id"],
    accessorKey: "report_id",
    canSort: false,
    width: 100,
    dataTransform: ({ report_id }) => ({
      cell: <ActionsCell report_id={report_id} />,
    }),
  };

const ScheduleCell = ({
  report_id,
  scheduled_report_id,
  frequency,
  week_day,
  month_day,
}: {
  report_id: string;
  scheduled_report_id: string;
  frequency: string;
  week_day: string;
  month_day: string;
}) => {
  const dispatch = useDispatch();
  const { fetchReport } = useReportContext();

  const onClose = () => {
    fetchReport();
    dispatch(closePopup());
  }

  const handleOpenSchedulePopup = () =>
    dispatch(createScheduleReportPopup(report_id, onClose));

  return (
    <>
      {scheduled_report_id ? (
        <Text
          style={{ cursor: "pointer" }}
          onClick={handleOpenSchedulePopup}
          color="primary"
        >
          {frequency === "MONTH" ? "Monthly" : "Weekly"}
        </Text>
      ) : (
        <Button
          variant="secondary"
          size="medium"
          onClick={handleOpenSchedulePopup}
        >
          + Schedule
        </Button>
      )}
    </>
  );
};

const StyledActionsCell = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 10px;
`;

const ActionsCell = ({ report_id }: { report_id: string }) => {
  const dispatch = useDispatch();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const { fetchReport } = useReportContext();

  const handleLoad = () => {
    dispatch(loadSavedReport(report_id));
  };
  const handleDelete = () => {
    setShowDeletePopup(true);
  };
  const handleClose = () => {
    setShowDeletePopup(false);
  };
  const handleRefresh = () => {
    fetchReport();
  };

  return (
    <StyledActionsCell>
      <Button mr={8} size="medium" onClick={handleLoad}>
        Load
      </Button>
      <IconButton preset="delete" onClick={handleDelete} />
      {showDeletePopup && (
        <DeleteReportPopup
          report_id={report_id}
          onClose={handleClose}
          refresh={handleRefresh}
        />
      )}
    </StyledActionsCell>
  );
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  ReportNameColumn,
  ReportTypeColumn,
  LastUpdatedColumn,
  ScheduleColumn,
  ActionsColumn,
];
